import React, { useState, useEffect } from 'react';
import './PartnerSelectionModal.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding, faCheckCircle, faCircle } from '@fortawesome/free-solid-svg-icons';

/**
 * PartnerSelectionModal Component
 * 
 * Modal for selecting which partner (buyer or seller) to create an invoice/PO for
 * when a delivery has multiple contractual partners.
 * Uses radio buttons for single selection.
 */
const PartnerSelectionModal = ({ 
  isOpen, 
  onClose, 
  partnerType, // 'buyer' or 'seller'
  partners = [], // List of partners from buyer_contractual_list or seller_contractual_list
  onSelect
}) => {
  const [selectedPartnerId, setSelectedPartnerId] = useState(null);

  // Reset selection when modal opens
  useEffect(() => {
    if (isOpen && partners.length > 0) {
      // Pre-select first partner by default
      setSelectedPartnerId(partners[0].crm_id);
    }
    
    if (!isOpen) {
      setSelectedPartnerId(null);
    }
  }, [isOpen, partners]);

  // Handle keyboard events
  useEffect(() => {
    if (!isOpen) return;
    
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'Enter' && selectedPartnerId) {
        handleConfirm();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, selectedPartnerId, onClose]);

  const handleConfirm = () => {
    if (!selectedPartnerId) return;
    onSelect(selectedPartnerId);
  };

  if (!isOpen) return null;

  const partnerLabel = partnerType === 'buyer' ? 'Customer' : 'Supplier';

  return (
    <div className="partner-selection-modal-overlay" onClick={onClose}>
      <div className="partner-selection-modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="partner-selection-modal-header">
          <h2>Select {partnerLabel}</h2>
          <button 
            className="partner-selection-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        {/* Body */}
        <div className="partner-selection-modal-body">
          <p className="partner-selection-description">
            This delivery has multiple {partnerType === 'buyer' ? 'buyers' : 'sellers'}. 
            Please select which {partnerLabel.toLowerCase()} this invoice should be created for:
          </p>

          <div className="partner-selection-list">
            {partners.map((partner) => (
              <div 
                key={partner.crm_id}
                className={`partner-selection-item ${selectedPartnerId === partner.crm_id ? 'selected' : ''}`}
                onClick={() => setSelectedPartnerId(partner.crm_id)}
              >
                <div className="partner-selection-radio">
                  <FontAwesomeIcon 
                    icon={selectedPartnerId === partner.crm_id ? faCheckCircle : faCircle} 
                  />
                </div>
                <div className="partner-selection-info">
                  <div className="partner-selection-icon">
                    <FontAwesomeIcon icon={faBuilding} />
                  </div>
                  <div className="partner-selection-details">
                    <div className="partner-selection-name">{partner.name}</div>
                    {partner.crm_id && (
                      <div className="partner-selection-crm-id">CRM ID: {partner.crm_id}</div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="partner-selection-modal-footer">
          <button 
            className="partner-selection-cancel-btn"
            onClick={onClose}
          >
            Cancel
          </button>
          <button 
            className="partner-selection-confirm-btn"
            onClick={handleConfirm}
            disabled={!selectedPartnerId}
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};

export default PartnerSelectionModal;
